/**
 * @file Контейнер для адаптивного отображения под устройство
 * @module membrane/src/components/DeviceContainer
 *
 * На мобильных: контент заполняет viewport напрямую (scale=1).
 * На десктопе: фиксированный размер, масштаб по cover.
 */

import { useState, useEffect, useRef } from 'react';

const CONTAINER_PORTRAIT = { width: 1080, height: 1920 };
const CONTAINER_LANDSCAPE = { width: 1920, height: 1080 };
const MOBILE_BREAKPOINT = 768;

function getScaleAndDims() {
  if (typeof window === 'undefined') {
    return { scale: 1, dimensions: CONTAINER_PORTRAIT, isMobile: false };
  }
  const vv = window.visualViewport;
  const w = vv ? vv.width : window.innerWidth;
  const h = vv ? vv.height : window.innerHeight;
  const isMobile = w <= MOBILE_BREAKPOINT;

  if (isMobile) {
    return {
      scale: 1,
      dimensions: { width: Math.round(w), height: Math.round(h) },
      isMobile: true,
    };
  }

  const isPortrait = h >= w;
  const dims = isPortrait ? CONTAINER_PORTRAIT : CONTAINER_LANDSCAPE;
  const s = Math.max(w / dims.width, h / dims.height);
  return { scale: s, dimensions: dims, isMobile: false };
}

export default function DeviceContainer({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(getScaleAndDims);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateScale = () => {
      setState(getScaleAndDims());
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateScale, 150);
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateScale);
      window.visualViewport.addEventListener('scroll', updateScale);
    }
    return () => {
      window.removeEventListener('resize', updateScale);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateScale);
        window.visualViewport.removeEventListener('scroll', updateScale);
      }
    };
  }, []);

  return (
    <div
      className="device-container-wrapper fixed inset-0 flex items-center justify-center overflow-hidden"
      style={{
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: state.dimensions.width,
          height: state.dimensions.height,
          transform: state.scale !== 1 ? `scale(${state.scale})` : undefined,
          transformOrigin: 'center center',
          flexShrink: 0,
        }}
        className="relative"
      >
        {children}
      </div>
    </div>
  );
}
